import { AppData } from '../types/AppData';
import { Step } from '../providers/globalState/GlobalStateProvider';

export interface StepAssets {
  immediateImages: string[];
  idleImages: string[];
  chunks: Array<{ key: string; factory: () => Promise<any> }>;
  lotties: string[];
  preloadPayment?: boolean;
}

// Chunk factories mirror the exact import paths from stepsView.tsx
// so webpack deduplicates them with the React.lazy() calls.
export const CHUNK_FACTORIES: Record<string, () => Promise<any>> = {
  startStep:         () => import('../components/steps/startStep'),
  language:          () => import('../components/steps/language'),
  nameFilling:       () => import('../components/steps/nameFilling'),
  personalPlan:      () => import('../components/steps/personalPlan'),
  singleChoiceView:  () => import('../components/shared/singleChoiceView'),
  howMulti:          () => import('../components/steps/howMulti'),
  learningEfficiency:() => import('../components/steps/learningEfficiency'),
  inGoodHands:       () => import('../components/steps/inGoodHands'),
  struggles:         () => import('../components/steps/struggles'),
  struggleView:      () => import('../components/steps/struggleView'),
  topicsOfInterest:  () => import('../components/steps/topicsOfInterest'),
  goalPractice:      () => import('../components/steps/goalPractice'),
  askAboutTest:      () => import('../components/steps/askAboutTest'),
  vocabularyTest:    () => import('../components/steps/vocabularyTest'),
  grammarTest:       () => import('../components/steps/grammarTest'),
  grammarTestResults: () => import('../components/steps/testResults'),
  lexTest:           () => import('../components/steps/lexTest'),
  lexTestResults:    () => import('../components/steps/lexTestResults'),
  creatingProgramV2: () => import('../components/steps/creatingProgramV2'),
  creatingProgram:   () => import('../components/steps/creatingProgram'),
  emailView:         () => import('../components/steps/emailView'),
  summary:           () => import('../components/steps/summary'),
  choosePlane:       () => import('../components/steps/choosePlane'),
  levelUp:           () => import('../components/steps/levelUp'),
};

const CDN = 'https://cdn-eu.lolaenglish.com/web-images%2F';

interface StepContext {
  data: AppData | null;
  selectedStruggles: number[];
  currentStruggleId: number | null;
  showLexTest: boolean;
  currentGrammarIndex: number;
  grammarQuestionCount: number;
  currentLexIndex: number;
  lexQuestionCount: number;
}

export function getStepAssets(currentStep: Step, ctx: StepContext): StepAssets {
  const assets: StepAssets = {
    immediateImages: [],
    idleImages: [],
    chunks: [],
    lotties: [],
  };

  const { data, selectedStruggles, currentStruggleId, showLexTest,
    currentGrammarIndex, grammarQuestionCount, currentLexIndex, lexQuestionCount } = ctx;

  switch (currentStep) {
    case 'start':
      // Next: language
      assets.chunks.push({ key: 'language', factory: CHUNK_FACTORIES.language });
      if (data?.languages) {
        assets.immediateImages.push(
          ...data.languages.map(l => l.icon).filter(Boolean) as string[]
        );
      }
      assets.chunks.push({ key: 'nameFilling', factory: CHUNK_FACTORIES.nameFilling });
      break;

    case 'language':
      // Next: name (video redirects to name)
      assets.chunks.push({ key: 'nameFilling', factory: CHUNK_FACTORIES.nameFilling });
      // Next+1: personalPlan
      assets.chunks.push({ key: 'personalPlan', factory: CHUNK_FACTORIES.personalPlan });
      assets.lotties.push('personal_plan');
      assets.immediateImages.push(`${CDN}img-plan.webp`);
      break;

    case 'name':
      // Next: personalPlan
      assets.chunks.push({ key: 'personalPlan', factory: CHUNK_FACTORIES.personalPlan });
      assets.lotties.push('personal_plan');
      assets.immediateImages.push(`${CDN}img-plan.webp`);
      // Next+1: level (uses singleChoiceView)
      assets.chunks.push({ key: 'singleChoiceView', factory: CHUNK_FACTORIES.singleChoiceView });
      assets.immediateImages.push(
        `${CDN}Beginner.svg`,
        `${CDN}Intermediate.svg`,
        `${CDN}Advansed.svg`,
      );
      break;

    case 'personalPlan':
      // Next: level
      assets.chunks.push({ key: 'singleChoiceView', factory: CHUNK_FACTORIES.singleChoiceView });
      assets.immediateImages.push(
        `${CDN}Beginner.svg`,
        `${CDN}Intermediate.svg`,
        `${CDN}Advansed.svg`,
      );
      // Next+1: how
      assets.chunks.push({ key: 'howMulti', factory: CHUNK_FACTORIES.howMulti });
      if (data?.hows) {
        assets.idleImages.push(...data.hows.map(h => h.image).filter(Boolean) as string[]);
      }
      break;

    case 'level':
      // Next: how
      assets.chunks.push({ key: 'howMulti', factory: CHUNK_FACTORIES.howMulti });
      if (data?.hows) {
        assets.immediateImages.push(...data.hows.map(h => h.image).filter(Boolean) as string[]);
      }
      // Next+1: learningEfficiencyFocus
      assets.chunks.push({ key: 'learningEfficiency', factory: CHUNK_FACTORIES.learningEfficiency });
      assets.idleImages.push(`${CDN}Lamp.webp`);
      break;

    case 'how':
      // Next: learningEfficiencyFocus
      assets.chunks.push({ key: 'learningEfficiency', factory: CHUNK_FACTORIES.learningEfficiency });
      assets.immediateImages.push(`${CDN}Lamp.webp`);
      // Next+1: why (uses singleChoiceView)
      assets.chunks.push({ key: 'singleChoiceView', factory: CHUNK_FACTORIES.singleChoiceView });
      if (data?.whys) {
        assets.idleImages.push(...data.whys.map(w => w.image).filter(Boolean) as string[]);
      }
      break;

    case 'learningEfficiencyFocus':
      // Next: why
      assets.chunks.push({ key: 'singleChoiceView', factory: CHUNK_FACTORIES.singleChoiceView });
      if (data?.whys) {
        assets.immediateImages.push(...data.whys.map(w => w.image).filter(Boolean) as string[]);
      }
      // Next+1: goodHands1
      assets.chunks.push({ key: 'inGoodHands', factory: CHUNK_FACTORIES.inGoodHands });
      assets.idleImages.push(
        'https://cdn-eu.lolaenglish.com/personal_plan.webp',
        `${CDN}1m.svg`,
        `${CDN}65000.svg`,
        `${CDN}4.8.svg`,
      );
      break;

    case 'why':
      // Next: goodHands1
      assets.chunks.push({ key: 'inGoodHands', factory: CHUNK_FACTORIES.inGoodHands });
      assets.immediateImages.push(
        'https://cdn-eu.lolaenglish.com/personal_plan.webp',
        `${CDN}1m.svg`,
        `${CDN}65000.svg`,
        `${CDN}4.8.svg`,
      );
      // Next+1: struggles
      assets.chunks.push({ key: 'struggles', factory: CHUNK_FACTORIES.struggles });
      if (data?.struggles) {
        assets.idleImages.push(...data.struggles.map(s => s.image).filter(Boolean) as string[]);
      }
      break;

    case 'goodHands1':
      // Next: struggles
      assets.chunks.push({ key: 'struggles', factory: CHUNK_FACTORIES.struggles });
      if (data?.struggles) {
        assets.immediateImages.push(
          ...data.struggles.map(s => s.image).filter(Boolean) as string[]
        );
        assets.idleImages.push(
          ...data.struggles.map(s => s.image_content).filter(Boolean) as string[]
        );
      }
      assets.chunks.push({ key: 'struggleView', factory: CHUNK_FACTORIES.struggleView });
      break;

    case 'struggles':
      // Next: struggleView (iterate selected struggles)
      assets.chunks.push({ key: 'struggleView', factory: CHUNK_FACTORIES.struggleView });
      if (data?.struggles && selectedStruggles.length > 0) {
        const selectedData = data.struggles.filter(s => selectedStruggles.includes(s.id));
        assets.immediateImages.push(
          ...selectedData.map(s => s.image_content).filter(Boolean) as string[]
        );
      }
      // Next+2: learningEfficiencyRegularStudy
      assets.chunks.push({ key: 'learningEfficiency', factory: CHUNK_FACTORIES.learningEfficiency });
      assets.idleImages.push(`${CDN}Brains.webp`, `${CDN}2x.webp`, `${CDN}presentation.svg`);
      break;

    case 'struggleView':
      if (currentStruggleId !== null && selectedStruggles.length > 0) {
        const currentIdx = selectedStruggles.indexOf(currentStruggleId);
        if (currentIdx < selectedStruggles.length - 1) {
          // More struggles -- preload next one's content image
          const nextStruggleId = selectedStruggles[currentIdx + 1];
          const nextStruggle = data?.struggles?.find(s => s.id === nextStruggleId);
          if (nextStruggle?.image_content) {
            assets.immediateImages.push(nextStruggle.image_content);
          }
        } else {
          // Last struggle -- preload next step
          assets.chunks.push({ key: 'learningEfficiency', factory: CHUNK_FACTORIES.learningEfficiency });
          assets.immediateImages.push(`${CDN}Brains.webp`, `${CDN}2x.webp`, `${CDN}presentation.svg`);
        }
      }
      assets.chunks.push({ key: 'topicsOfInterest', factory: CHUNK_FACTORIES.topicsOfInterest });
      if (data?.topics) {
        assets.idleImages.push(...data.topics.map(t => t.image).filter(Boolean) as string[]);
      }
      break;

    case 'learningEfficiencyRegularStudy':
      // Next: topics
      assets.chunks.push({ key: 'topicsOfInterest', factory: CHUNK_FACTORIES.topicsOfInterest });
      if (data?.topics) {
        assets.immediateImages.push(...data.topics.map(t => t.image).filter(Boolean) as string[]);
      }
      // Next+1: goal
      assets.chunks.push({ key: 'goalPractice', factory: CHUNK_FACTORIES.goalPractice });
      assets.idleImages.push(`${CDN}30.webp`, `${CDN}15.webp`, `${CDN}5.webp`);
      break;

    case 'topics':
      // Next: goal
      assets.chunks.push({ key: 'goalPractice', factory: CHUNK_FACTORIES.goalPractice });
      assets.immediateImages.push(`${CDN}30.webp`, `${CDN}15.webp`, `${CDN}5.webp`);
      // Next+1: askTest
      assets.chunks.push({ key: 'askAboutTest', factory: CHUNK_FACTORIES.askAboutTest });
      assets.idleImages.push(`${CDN}brain.webp`);
      break;

    case 'goal':
      // Next: askTest
      assets.chunks.push({ key: 'askAboutTest', factory: CHUNK_FACTORIES.askAboutTest });
      assets.immediateImages.push(`${CDN}brain.webp`);
      // Next+1: vocabularyTest
      assets.chunks.push({ key: 'vocabularyTest', factory: CHUNK_FACTORIES.vocabularyTest });
      break;

    case 'askTest':
      assets.chunks.push({ key: 'vocabularyTest', factory: CHUNK_FACTORIES.vocabularyTest });
      assets.chunks.push({ key: 'grammarTestResults', factory: CHUNK_FACTORIES.grammarTestResults });
      break;

    case 'vocabularyTestBegginer':
    case 'vocabularyTestIntermediate':
      assets.chunks.push({ key: 'grammarTestResults', factory: CHUNK_FACTORIES.grammarTestResults });
      break;

    case 'vocabularyTestAdvanced':
      // Next: testResults OR grammarTest/0
      assets.chunks.push({ key: 'grammarTestResults', factory: CHUNK_FACTORIES.grammarTestResults });
      assets.chunks.push({ key: 'grammarTest', factory: CHUNK_FACTORIES.grammarTest });
      // Start preloading first grammar question emojis
      if (data?.grammar_test?.questions) {
        for (const q of data.grammar_test.questions.slice(0, 3)) {
          if (q.options) {
            assets.idleImages.push(
              ...q.options.map(o => o.emoji).filter(Boolean) as string[]
            );
          }
        }
      }
      break;

    case 'testResults':
      // Next: grammarTest/0
      assets.chunks.push({ key: 'grammarTest', factory: CHUNK_FACTORIES.grammarTest });
      if (data?.grammar_test?.questions) {
        // Immediately preload first few questions' emojis
        for (const q of data.grammar_test.questions.slice(0, 5)) {
          if (q.options) {
            assets.immediateImages.push(
              ...q.options.map(o => o.emoji).filter(Boolean) as string[]
            );
          }
        }
        // Idle-preload remaining
        for (const q of data.grammar_test.questions.slice(5)) {
          if (q.options) {
            assets.idleImages.push(
              ...q.options.map(o => o.emoji).filter(Boolean) as string[]
            );
          }
        }
      }
      break;

    case 'grammarTest':
      // Preload next grammar question emojis
      if (data?.grammar_test?.questions) {
        const nextQ = data.grammar_test.questions[currentGrammarIndex + 1];
        if (nextQ?.options) {
          assets.immediateImages.push(
            ...nextQ.options.map(o => o.emoji).filter(Boolean) as string[]
          );
        }
        const twoAhead = data.grammar_test.questions[currentGrammarIndex + 2];
        if (twoAhead?.options) {
          assets.idleImages.push(
            ...twoAhead.options.map(o => o.emoji).filter(Boolean) as string[]
          );
        }
        // Near the end, preload grammarTestResults
        if (currentGrammarIndex >= grammarQuestionCount - 3) {
          assets.chunks.push({ key: 'grammarTestResults', factory: CHUNK_FACTORIES.grammarTestResults });
          assets.preloadPayment = true;
        }
      }
      break;

    case 'grammarTestResults':
      assets.preloadPayment = true;
      if (showLexTest) {
        // Next: lexTest
        assets.chunks.push({ key: 'lexTest', factory: CHUNK_FACTORIES.lexTest });
        if (data?.lex_test?.questions) {
          for (const q of data.lex_test.questions.slice(0, 3)) {
            if (q.image) assets.immediateImages.push(q.image);
            if (q.images) assets.immediateImages.push(...q.images);
            if (q.options) {
              assets.immediateImages.push(
                ...q.options.map(o => o.image).filter(Boolean) as string[]
              );
            }
          }
        }
        assets.chunks.push({ key: 'lexTestResults', factory: CHUNK_FACTORIES.lexTestResults });
        assets.lotties.push('Time', 'Percent_Correct');
        assets.idleImages.push(`${CDN}positive.webp`, `${CDN}Negative.webp`);
      } else {
        // Next: creatProgram
        assets.chunks.push({ key: 'creatingProgramV2', factory: CHUNK_FACTORIES.creatingProgramV2 });
        assets.chunks.push({ key: 'creatingProgram', factory: CHUNK_FACTORIES.creatingProgram });
        assets.lotties.push('prog_step_1', 'prog_step_2', 'prog_step_3');
        assets.immediateImages.push(
          `${CDN}prog_step_1.webp`,
          `${CDN}prog_step_2.webp`,
          `${CDN}prog_step_2_q.webp`,
        );
        assets.idleImages.push(
          `${CDN}prog_step_3.webp`, `${CDN}prog_step_3_q.webp`,
          `${CDN}prog_step_4.webp`, `${CDN}prog_step_4_q.webp`,
          `${CDN}prog_step_5.webp`, `${CDN}prog_step_5_q.webp`,
          `${CDN}prog_step_6.webp`, `${CDN}prog_step_6_q.webp`,
        );
      }
      break;

    case 'lexTest':
      if (data?.lex_test?.questions) {
        const nextQ = data.lex_test.questions[currentLexIndex + 1];
        if (nextQ) {
          if (nextQ.image) assets.immediateImages.push(nextQ.image);
          if (nextQ.images) assets.immediateImages.push(...nextQ.images);
          if (nextQ.options) {
            assets.immediateImages.push(
              ...nextQ.options.map(o => o.image).filter(Boolean) as string[]
            );
          }
        }
        const twoAhead = data.lex_test.questions[currentLexIndex + 2];
        if (twoAhead) {
          if (twoAhead.image) assets.idleImages.push(twoAhead.image);
          if (twoAhead.images) assets.idleImages.push(...twoAhead.images);
          if (twoAhead.options) {
            assets.idleImages.push(
              ...twoAhead.options.map(o => o.image).filter(Boolean) as string[]
            );
          }
        }
        if (currentLexIndex >= lexQuestionCount - 3) {
          assets.chunks.push({ key: 'lexTestResults', factory: CHUNK_FACTORIES.lexTestResults });
          assets.lotties.push('Time', 'Percent_Correct');
          assets.preloadPayment = true;
        }
      }
      break;

    case 'lexTestResults':
      // Next: creatProgram
      assets.chunks.push({ key: 'creatingProgramV2', factory: CHUNK_FACTORIES.creatingProgramV2 });
      assets.chunks.push({ key: 'creatingProgram', factory: CHUNK_FACTORIES.creatingProgram });
      assets.lotties.push('prog_step_1', 'prog_step_2', 'prog_step_3', 'prog_step_4', 'prog_step_5', 'prog_step_6');
      assets.immediateImages.push(
        `${CDN}prog_step_1.webp`, `${CDN}prog_step_2.webp`, `${CDN}prog_step_2_q.webp`,
      );
      assets.idleImages.push(
        `${CDN}prog_step_3.webp`, `${CDN}prog_step_3_q.webp`,
        `${CDN}prog_step_4.webp`, `${CDN}prog_step_4_q.webp`,
        `${CDN}prog_step_5.webp`, `${CDN}prog_step_5_q.webp`,
        `${CDN}prog_step_6.webp`, `${CDN}prog_step_6_q.webp`,
      );
      assets.preloadPayment = true;
      break;

    case 'creatProgram':
      // Long autoplay step -- excellent time to preload ahead
      assets.chunks.push({ key: 'emailView', factory: CHUNK_FACTORIES.emailView });
      assets.immediateImages.push(`${CDN}ai-video.svg`);
      assets.lotties.push('prog_step_4', 'prog_step_5', 'prog_step_6');
      // Preload summary + choosePlan during this long step
      assets.chunks.push({ key: 'summary', factory: CHUNK_FACTORIES.summary });
      assets.chunks.push({ key: 'choosePlane', factory: CHUNK_FACTORIES.choosePlane });
      assets.idleImages.push(
        `${CDN}startBrain.webp`,
        `${CDN}gift.webp`,
        `${CDN}safe.svg`,
        `${CDN}twigs.webp`,
        `${CDN}store.webp`,
      );
      // Start warming up payment connection
      assets.preloadPayment = true;
      break;

    case 'email':
      // Next: summary
      assets.chunks.push({ key: 'summary', factory: CHUNK_FACTORIES.summary });
      assets.immediateImages.push(`${CDN}startBrain.webp`);
      // Next+1: choosePlan (heavy)
      assets.chunks.push({ key: 'choosePlane', factory: CHUNK_FACTORIES.choosePlane });
      assets.idleImages.push(
        `${CDN}gift.webp`,
        `${CDN}safe.svg`,
        `${CDN}twigs.webp`,
        `${CDN}store.webp`,
      );
      assets.preloadPayment = true;
      break;

    case 'summary':
      // Next: choosePlan (heavy chunk)
      assets.chunks.push({ key: 'choosePlane', factory: CHUNK_FACTORIES.choosePlane });
      assets.immediateImages.push(
        `${CDN}gift.webp`,
        `${CDN}safe.svg`,
        `${CDN}twigs.webp`,
        `${CDN}store.webp`,
      );
      assets.lotties.push('Badge_opt');
      assets.preloadPayment = true;
      break;

    case 'choosePlan':
      assets.preloadPayment = true;
      // Terminal step
      break;
  }

  return assets;
}
